import React from 'react';
import {
    Alert,
    Card,
    Col,
    Empty,
    InputNumber,
    Radio,
    Row,
    Skeleton,
    Space,
    Switch,
    Tabs,
    Typography,
} from 'antd';
import {
    BarChartOutlined,
    DollarOutlined,
    InfoCircleOutlined,
    SafetyOutlined,
    StopOutlined,
} from '@ant-design/icons';
import { useSubsystemEditorContext } from '../hooks/useSubsystemDraftEditor';
import { SubsystemDraftActions } from '../components/SubsystemDraftActions';

const { Text, Paragraph } = Typography;

interface TokenQuotasPayload {
    schemaVersion: 1;
    systemSettingsKeys: Record<string, string | undefined>;
    configTableRows: Record<string, never>;
}

function asBool(v: string | undefined): boolean {
    return (v ?? 'false').toString().toLowerCase() === 'true';
}

function asInt(v: string | undefined, fallback = 0): number {
    if (v === undefined || v === null || v === '') return fallback;
    const n = Number(v);
    return Number.isFinite(n) ? Math.round(n) : fallback;
}

function asNumber(v: string | undefined, fallback = 0): number {
    if (v === undefined || v === null || v === '') return fallback;
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
}

/**
 * Configure tab body for Token Quotas & Cost Control.
 *
 * Pure system_settings — no config tables. Vertical sidebar tabs split the
 * 18 keys into four logical groups: tracking / tier quotas / enforcement &
 * alerts / sandbox cost. There's no legacy admin page to decommission;
 * these were only editable from the raw SystemSettingsPage row table.
 */
export const TokenQuotasConfigurePage: React.FC = () => {
    const editor = useSubsystemEditorContext<TokenQuotasPayload>();

    if (editor.isLoading || !editor.payload) {
        return <Skeleton active paragraph={{ rows: 12 }} />;
    }

    const settings = editor.payload.systemSettingsKeys ?? {};
    if (!settings || Object.keys(settings).length === 0) {
        return <Empty description="No token-quota settings — has the v1 baseline been seeded?" />;
    }

    const setSetting = (key: string, value: string) => {
        editor.setPayload((prev) => ({
            ...prev!,
            systemSettingsKeys: { ...prev!.systemSettingsKeys, [key]: value },
        }));
    };

    return (
        <div>
            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
                <Text type="secondary">
                    Per-tier token quotas, usage monitoring, enforcement thresholds, alert
                    routing, and sandbox cost guardrails. All settings are platform-wide; per-org
                    overrides live on the organisation record.
                </Text>
                <SubsystemDraftActions
                    detail={editor.detail}
                    isDirty={editor.isDirty}
                    isSaving={editor.isSaving}
                    isPublishing={editor.isPublishing}
                    isDiscarding={editor.isDiscarding}
                    onSaveDraft={() => editor.saveDraft()}
                    onDiscardDraft={() => editor.discardDraft()}
                />
            </Space>

            <Card>
                <Tabs
                    tabPosition="left"
                    style={{ minHeight: 600 }}
                    items={[
                        {
                            key: 'tracking',
                            label: (
                                <span>
                                    <BarChartOutlined style={{ marginRight: 8, color: '#3b82f6' }} />
                                    Tracking
                                </span>
                            ),
                            children: <TrackingTab settings={settings} setSetting={setSetting} />,
                        },
                        {
                            key: 'quotas',
                            label: (
                                <span>
                                    <SafetyOutlined style={{ marginRight: 8, color: '#10b981' }} />
                                    Tier quotas
                                </span>
                            ),
                            children: <QuotasTab settings={settings} setSetting={setSetting} />,
                        },
                        {
                            key: 'enforcement',
                            label: (
                                <span>
                                    <StopOutlined style={{ marginRight: 8, color: '#ef4444' }} />
                                    Enforcement & alerts
                                </span>
                            ),
                            children: (
                                <EnforcementTab settings={settings} setSetting={setSetting} />
                            ),
                        },
                        {
                            key: 'sandbox',
                            label: (
                                <span>
                                    <DollarOutlined style={{ marginRight: 8, color: '#f97316' }} />
                                    Sandbox cost
                                </span>
                            ),
                            children: <SandboxTab settings={settings} setSetting={setSetting} />,
                        },
                    ]}
                />
            </Card>
        </div>
    );
};

interface SettingsTabProps {
    settings: Record<string, string | undefined>;
    setSetting: (key: string, value: string) => void;
}

const TrackingTab: React.FC<SettingsTabProps> = ({ settings, setSetting }) => {
    const tracking = asBool(settings.AI_TOKEN_TRACKING_ENABLED);
    const monitoring = asBool(settings.AI_USAGE_MONITORING_ENABLED);
    const alertThresholdPct = Math.round(asNumber(settings.AI_USAGE_ALERT_THRESHOLD, 0.8) * 100);

    return (
        <div>
            <Alert
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                style={{ marginBottom: 16 }}
                message="Usage tracking & monitoring"
                description="Whether the platform records token consumption per call and tracks running usage against quotas. Disabling tracking effectively disables every quota check downstream."
            />

            <Row gutter={16}>
                <Col xs={24} md={12}>
                    <Space direction="vertical" size={2}>
                        <Text strong>Token tracking enabled</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            <code>AI_TOKEN_TRACKING_ENABLED</code> — record token usage per call.
                        </Text>
                        <Switch
                            checked={tracking}
                            onChange={(checked) =>
                                setSetting('AI_TOKEN_TRACKING_ENABLED', checked ? 'true' : 'false')
                            }
                        />
                    </Space>
                </Col>
                <Col xs={24} md={12}>
                    <Space direction="vertical" size={2}>
                        <Text strong>Usage monitoring enabled</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            <code>AI_USAGE_MONITORING_ENABLED</code> — track running totals
                            against quotas.
                        </Text>
                        <Switch
                            checked={monitoring}
                            onChange={(checked) =>
                                setSetting('AI_USAGE_MONITORING_ENABLED', checked ? 'true' : 'false')
                            }
                        />
                    </Space>
                </Col>
            </Row>

            <div style={{ marginTop: 24 }}>
                <Text strong>Usage alert threshold</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                    <code>AI_USAGE_ALERT_THRESHOLD</code> — percentage of quota at which a
                    monitoring alert fires (stored as a decimal, 0–1).
                </Text>
                <br />
                <InputNumber
                    min={0}
                    max={100}
                    step={5}
                    style={{ marginTop: 4, width: 160 }}
                    value={alertThresholdPct}
                    onChange={(v) =>
                        setSetting(
                            'AI_USAGE_ALERT_THRESHOLD',
                            String(Math.max(0, Math.min(100, Number(v ?? 0))) / 100),
                        )
                    }
                    formatter={(v) => `${v ?? 0}%`}
                    parser={(v) => Number((v ?? '0').toString().replace('%', ''))}
                />
            </div>
        </div>
    );
};

const QuotasTab: React.FC<SettingsTabProps> = ({ settings, setSetting }) => {
    const tiers: Array<{ tier: string; dailyKey: string; monthlyKey: string }> = [
        {
            tier: 'Basic',
            dailyKey: 'AI_QUOTA_BASIC_DAILY',
            monthlyKey: 'AI_QUOTA_BASIC_MONTHLY',
        },
        {
            tier: 'Professional',
            dailyKey: 'AI_QUOTA_PROFESSIONAL_DAILY',
            monthlyKey: 'AI_QUOTA_PROFESSIONAL_MONTHLY',
        },
        {
            tier: 'Enterprise',
            dailyKey: 'AI_QUOTA_ENTERPRISE_DAILY',
            monthlyKey: 'AI_QUOTA_ENTERPRISE_MONTHLY',
        },
    ];

    return (
        <div>
            <Alert
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                style={{ marginBottom: 16 }}
                message="Per-tier token quotas (tokens, not dollars)"
                description="Daily and monthly token caps applied per organisation based on its subscription tier. The default keys are used when a tier is unrecognised or unset."
            />

            {tiers.map(({ tier, dailyKey, monthlyKey }) => (
                <Card
                    key={tier}
                    size="small"
                    title={tier}
                    style={{ marginBottom: 12 }}
                >
                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Text strong>Daily quota</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                <code>{dailyKey}</code>
                            </Text>
                            <br />
                            <InputNumber
                                min={0}
                                step={1000}
                                style={{ width: 200, marginTop: 4 }}
                                value={asInt(settings[dailyKey])}
                                onChange={(v) => setSetting(dailyKey, String(v ?? 0))}
                                formatter={(v) =>
                                    `${(v ?? 0).toLocaleString()}`
                                }
                                parser={(v) =>
                                    Number((v ?? '0').toString().replace(/,/g, '')) || 0
                                }
                            />
                        </Col>
                        <Col xs={24} md={12}>
                            <Text strong>Monthly quota</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                <code>{monthlyKey}</code>
                            </Text>
                            <br />
                            <InputNumber
                                min={0}
                                step={10000}
                                style={{ width: 200, marginTop: 4 }}
                                value={asInt(settings[monthlyKey])}
                                onChange={(v) => setSetting(monthlyKey, String(v ?? 0))}
                                formatter={(v) =>
                                    `${(v ?? 0).toLocaleString()}`
                                }
                                parser={(v) =>
                                    Number((v ?? '0').toString().replace(/,/g, '')) || 0
                                }
                            />
                        </Col>
                    </Row>
                </Card>
            ))}

            <Card size="small" title="Defaults (fallback when tier is unset)">
                <Row gutter={16}>
                    <Col xs={24} md={12}>
                        <Text strong>Default daily token limit</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            <code>AI_DAILY_TOKEN_LIMIT_DEFAULT</code>
                        </Text>
                        <br />
                        <InputNumber
                            min={0}
                            step={1000}
                            style={{ width: 200, marginTop: 4 }}
                            value={asInt(settings.AI_DAILY_TOKEN_LIMIT_DEFAULT)}
                            onChange={(v) => setSetting('AI_DAILY_TOKEN_LIMIT_DEFAULT', String(v ?? 0))}
                            formatter={(v) => `${(v ?? 0).toLocaleString()}`}
                            parser={(v) => Number((v ?? '0').toString().replace(/,/g, '')) || 0}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <Text strong>Default monthly token limit</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            <code>AI_MONTHLY_TOKEN_LIMIT_DEFAULT</code>
                        </Text>
                        <br />
                        <InputNumber
                            min={0}
                            step={10000}
                            style={{ width: 200, marginTop: 4 }}
                            value={asInt(settings.AI_MONTHLY_TOKEN_LIMIT_DEFAULT)}
                            onChange={(v) =>
                                setSetting('AI_MONTHLY_TOKEN_LIMIT_DEFAULT', String(v ?? 0))
                            }
                            formatter={(v) => `${(v ?? 0).toLocaleString()}`}
                            parser={(v) => Number((v ?? '0').toString().replace(/,/g, '')) || 0}
                        />
                    </Col>
                </Row>
            </Card>
        </div>
    );
};

const EnforcementTab: React.FC<SettingsTabProps> = ({ settings, setSetting }) => {
    const enforcement = asBool(settings.AI_QUOTA_ENFORCEMENT_ENABLED);
    const sendAlerts = asBool(settings.AI_QUOTA_SEND_ALERTS);
    const recipients = settings.AI_QUOTA_ALERT_RECIPIENTS ?? 'org_admins';
    const warningPct = Math.round(asNumber(settings.AI_QUOTA_WARNING_THRESHOLD, 0.9) * 100);
    const alertPct = Math.round(asNumber(settings.AI_QUOTA_ALERT_THRESHOLD, 0.8) * 100);

    return (
        <div>
            <Alert
                type="warning"
                showIcon
                icon={<InfoCircleOutlined />}
                style={{ marginBottom: 16 }}
                message="Enforcement is hard — once a quota is breached, calls are rejected"
                description="With enforcement on, customers above their daily/monthly cap will see hard failures from AI features. Use the warning + alert thresholds to give them a heads-up first. Turn enforcement off to monitor without blocking."
            />

            <Row gutter={16}>
                <Col xs={24} md={12}>
                    <Space direction="vertical" size={2}>
                        <Text strong>Quota enforcement enabled</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            <code>AI_QUOTA_ENFORCEMENT_ENABLED</code>
                        </Text>
                        <Switch
                            checked={enforcement}
                            onChange={(checked) =>
                                setSetting('AI_QUOTA_ENFORCEMENT_ENABLED', checked ? 'true' : 'false')
                            }
                        />
                    </Space>
                </Col>
                <Col xs={24} md={12}>
                    <Space direction="vertical" size={2}>
                        <Text strong>Send quota alerts</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            <code>AI_QUOTA_SEND_ALERTS</code> — emit notifications when a
                            threshold is crossed.
                        </Text>
                        <Switch
                            checked={sendAlerts}
                            onChange={(checked) =>
                                setSetting('AI_QUOTA_SEND_ALERTS', checked ? 'true' : 'false')
                            }
                        />
                        <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>
                            Independent of enforcement — leave alerts on with enforcement off
                            for a "monitor only" rollout.
                        </Text>
                    </Space>
                </Col>
            </Row>

            <div style={{ marginTop: 24 }}>
                <Row gutter={16}>
                    <Col xs={24} md={12}>
                        <Text strong>Alert threshold</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            <code>AI_QUOTA_ALERT_THRESHOLD</code> — first notification at this %
                            of quota.
                        </Text>
                        <br />
                        <InputNumber
                            min={0}
                            max={100}
                            step={5}
                            style={{ width: 140, marginTop: 4 }}
                            value={alertPct}
                            onChange={(v) =>
                                setSetting(
                                    'AI_QUOTA_ALERT_THRESHOLD',
                                    String(Math.max(0, Math.min(100, Number(v ?? 0))) / 100),
                                )
                            }
                            formatter={(v) => `${v ?? 0}%`}
                            parser={(v) => Number((v ?? '0').toString().replace('%', ''))}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <Text strong>Warning threshold</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            <code>AI_QUOTA_WARNING_THRESHOLD</code> — escalated warning at this
                            %.
                        </Text>
                        <br />
                        <InputNumber
                            min={0}
                            max={100}
                            step={5}
                            style={{ width: 140, marginTop: 4 }}
                            value={warningPct}
                            onChange={(v) =>
                                setSetting(
                                    'AI_QUOTA_WARNING_THRESHOLD',
                                    String(Math.max(0, Math.min(100, Number(v ?? 0))) / 100),
                                )
                            }
                            formatter={(v) => `${v ?? 0}%`}
                            parser={(v) => Number((v ?? '0').toString().replace('%', ''))}
                        />
                    </Col>
                </Row>
            </div>

            <div style={{ marginTop: 24 }}>
                <Text strong>Alert recipients</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                    <code>AI_QUOTA_ALERT_RECIPIENTS</code> — who is notified when an alert
                    fires.
                </Text>
                <br />
                <Radio.Group
                    style={{ marginTop: 4 }}
                    value={recipients}
                    onChange={(e) => setSetting('AI_QUOTA_ALERT_RECIPIENTS', e.target.value)}
                    options={[
                        { value: 'org_admins', label: 'Organisation admins only' },
                        { value: 'all_users', label: 'All users in the organisation' },
                    ]}
                    optionType="button"
                />
            </div>
        </div>
    );
};

const SandboxTab: React.FC<SettingsTabProps> = ({ settings, setSetting }) => {
    const sandboxThreshold = asNumber(settings.AI_SANDBOX_DAILY_ALERT_THRESHOLD_USD, 0);
    // Default to platform_admins — the org_admins value the seeder may have copied
    // from AI_QUOTA_ALERT_RECIPIENTS doesn't make sense here: the sandbox is
    // SUPER_ADMIN/ADMIN only, so customer-org admins shouldn't be notified.
    const sandboxRecipients = settings.AI_SANDBOX_ALERT_RECIPIENTS || 'platform_admins';

    return (
        <div>
            <Alert
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                style={{ marginBottom: 16 }}
                message="Sandbox cost guardrails"
                description="The Phase 4 sandbox lets platform/super admins replay AI calls against draft prompts. These two settings cap how much sandbox spend is acceptable per day before an alert is sent — a safety net since sandbox runs are billed but not enforced via the per-tier quotas above."
            />

            <Row gutter={16}>
                <Col xs={24} md={12}>
                    <Text strong>Daily sandbox alert threshold ($)</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        <code>AI_SANDBOX_DAILY_ALERT_THRESHOLD_USD</code> — alert fires when
                        sandbox spend across all admins exceeds this in a 24h window.
                    </Text>
                    <br />
                    <InputNumber
                        min={0}
                        step={5}
                        style={{ width: 200, marginTop: 4 }}
                        value={sandboxThreshold}
                        onChange={(v) =>
                            setSetting('AI_SANDBOX_DAILY_ALERT_THRESHOLD_USD', String(v ?? 0))
                        }
                        addonBefore="$"
                    />
                </Col>
                <Col xs={24} md={12}>
                    <Text strong>Sandbox alert recipients</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        <code>AI_SANDBOX_ALERT_RECIPIENTS</code> — sandbox is admin-only, so
                        customer-org admins are never recipients.
                    </Text>
                    <br />
                    <Radio.Group
                        style={{ marginTop: 4 }}
                        value={sandboxRecipients}
                        onChange={(e) =>
                            setSetting('AI_SANDBOX_ALERT_RECIPIENTS', e.target.value)
                        }
                        options={[
                            { value: 'super_admins', label: 'SUPER_ADMIN only' },
                            { value: 'platform_admins', label: 'SUPER_ADMIN + ADMIN' },
                        ]}
                        optionType="button"
                    />
                </Col>
            </Row>

            <Paragraph type="secondary" style={{ marginTop: 16 }}>
                Phase 4 (sandbox) is not yet shipped, so these values aren't read by anything
                today. Configuring them now means the alert is in place the moment the sandbox
                lands.
            </Paragraph>
        </div>
    );
};
